/*
  Lux Health storefront behaviour.
  Vanilla custom elements, Dawn conventions, quiet motion (120-200ms).
*/

(function () {
  'use strict';

  const CART_DRAWER = () => document.querySelector('cart-drawer');
  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------------------------------------------------------------- *
   * Toast
   * ---------------------------------------------------------------- */

  let toastTimer = null;

  function luxToast(message, actionLabel, onAction) {
    let host = document.getElementById('LuxToast');
    if (!host) {
      host = document.createElement('div');
      host.id = 'LuxToast';
      host.className = 'lux-toast';
      host.setAttribute('role', 'status');
      host.setAttribute('aria-live', 'polite');
      document.body.appendChild(host);
    }

    host.innerHTML = '';
    const dot = document.createElement('span');
    dot.className = 'lux-toast__dot';
    const text = document.createElement('span');
    text.textContent = message;
    host.appendChild(dot);
    host.appendChild(text);

    if (actionLabel) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lux-toast__action';
      button.textContent = actionLabel;
      button.addEventListener('click', () => {
        host.classList.remove('lux-toast--visible');
        if (typeof onAction === 'function') onAction();
      });
      host.appendChild(button);
    }

    requestAnimationFrame(() => host.classList.add('lux-toast--visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => host.classList.remove('lux-toast--visible'), 2600);
  }

  window.luxToast = luxToast;

  /* ---------------------------------------------------------------- *
   * Add to cart (product cards + PDP)
   * ---------------------------------------------------------------- */

  class LuxAddToCart extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.querySelector('[type="submit"]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

      const cart = CART_DRAWER();
      const formData = new FormData(this.form);

      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      this.setLoading(true);

      fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then(async (response) => {
          if (response.status) {
            this.showError(response.description || response.message);
            return;
          }

          // The gift add returns the cart sections too, and is the later state,
          // so the drawer renders from it. Listeners still get the pack's line.
          const giftResponse = await this.addGift(formData.get('id'), cart);

          // constants.js declares PUB_SUB_EVENTS with const: a global binding,
          // not a window property, so test the binding itself.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'lux-add-to-cart',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          cart.classList.remove('is-empty');
          cart.renderContents(giftResponse || response);

          if (this.hasAttribute('data-quiet')) {
            luxToast(window.luxStrings.addedToCart, window.luxStrings.viewCart, () => cart.open());
          }

          this.showSelleasyOffer();
        })
        .catch((error) => {
          console.error(error);
          this.showError();
        })
        .finally(() => this.setLoading(false));
    }

    /* A pack that comes with a free gift also puts the gift in the cart, so the
     * automatic discount has a line to make free. The section marks the
     * element with data-gift-triggers (the pack's variant ids) and
     * data-gift-variants (the gift variants in stock, in order of
     * preference). One gift per pack: the cart is topped up until it holds
     * as many gifts as packs, counting gifts already there, so two packs get
     * two gifts and a repeat add does not double up. Resolves to the last
     * gift add's response, which carries the cart sections, or null when
     * nothing was added. Each gift line carries a hidden _gift_for property
     * (the pack's variant id) so trimGifts() below can take it back out.
     */
    async addGift(variantId, cart) {
      const split = (value) => (value || '').split(',').filter(Boolean);
      const triggers = split(this.dataset.giftTriggers);
      const gifts = split(this.dataset.giftVariants);
      if (!gifts.length || !triggers.includes(String(variantId))) return null;

      try {
        const current = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
        const needed = countLines(current.items, triggers) - countLines(current.items, gifts);
        return await addGiftLines(variantId, gifts, needed, cart);
      } catch (error) {
        console.error(error);
        return null;
      }
    }

    /* Selleasy's add-to-cart upsell popup. Selleasy normally catches the click
     * on button[name="add"] before the add; when it does, it marks the button
     * lb-atc-listener and runs its own popup, so skip it here or the shopper
     * sees the popup twice. Otherwise ask for it after the add, through the
     * same lbHandlers.action(undefined, true) call Selleasy wires up for
     * custom carts (iCart, UpCart). Selleasy shows nothing when no add-to-cart
     * offer matches the product, so this is safe on every product.
     */
    showSelleasyOffer() {
      const handlers = window.lbHandlers;
      if (!handlers || typeof handlers.action !== 'function') return;
      if (this.submitButton && this.submitButton.hasAttribute('lb-atc-listener')) return;

      try {
        handlers.action(undefined, true);
      } catch (error) {
        console.error(error);
      }
    }

    setLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('lux-btn--loading', loading);
      if (loading) {
        this.submitButton.setAttribute('aria-disabled', 'true');
      } else {
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    showError(message) {
      const target = this.querySelector('[data-lux-form-error]');
      const text = message || window.luxStrings.genericError;
      if (target) {
        target.textContent = text;
        target.hidden = false;
      } else {
        luxToast(text);
      }
    }
  }

  if (!customElements.get('lux-add-to-cart')) customElements.define('lux-add-to-cart', LuxAddToCart);

  /* ---------------------------------------------------------------- *
   * Free gifts in the cart
   *
   * A gift added with its pack carries _gift_for, the variant id of the
   * pack, hidden from the cart by its leading underscore. Two rules keep the
   * cart at one gift per pack:
   *
   * - Trim: when the cart holds more of those gift lines than packs (the
   *   pack was lowered or removed), the extra gifts come out, so nobody pays
   *   for a tube they did not choose. Tubes a shopper added themselves carry
   *   no _gift_for and are never touched.
   * - Top up: when a pack's quantity goes up in the cart (the + button or a
   *   typed quantity), gifts are added to match. Only on an increase, so a
   *   shopper who deletes a gift does not get it straight back.
   *
   * Which packs come with which gifts is in window.luxGiftMap, written by
   * snippets/lux-gift-map.liquid for the viewed product and the cart's.
   * ---------------------------------------------------------------- */

  function onCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  function countLines(items, ids) {
    const wanted = ids.map(String);
    return items
      .filter((item) => wanted.includes(String(item.variant_id)))
      .reduce((total, item) => total + item.quantity, 0);
  }

  /* Adds `needed` gifts for a pack: all at once from the first gift with
   * stock, and when one runs short part way, one at a time, then the next
   * gift. Resolves to the last successful add's response (with the cart
   * sections when a drawer is given), or null when nothing was added. */
  async function addGiftLines(packId, gifts, needed, cart) {
    const add = (id, quantity) => {
      const body = { items: [{ id: Number(id), quantity, properties: { _gift_for: String(packId) } }] };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      return fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json());
    };

    let last = null;
    for (const id of gifts) {
      if (needed <= 0) break;
      const response = await add(id, needed);
      if (!response.status) {
        last = response;
        needed = 0;
        break;
      }
      while (needed > 0) {
        const one = await add(id, 1);
        if (one.status) break;
        last = one;
        needed -= 1;
      }
    }
    return last;
  }

  // Pack quantities as of the last cart state seen, to tell an increase from
  // a decrease. Seeded from the cart on load.
  let knownPacks = {};

  function packTotals(items) {
    const totals = {};
    items.forEach((item) => {
      totals[item.variant_id] = (totals[item.variant_id] || 0) + item.quantity;
    });
    return totals;
  }

  function refreshKnownPacks() {
    fetch(`${window.routes.cart_url}.js`)
      .then((r) => r.json())
      .then((state) => {
        knownPacks = packTotals(state.items || []);
      })
      .catch(() => {});
  }

  function syncGifts(event) {
    // An add from a product page carries the added line, not the cart.
    if (event && event.source === 'lux-add-to-cart') {
      refreshKnownPacks();
      return;
    }

    const state = event && event.cartData;
    if (!state || !Array.isArray(state.items)) return;

    const packs = packTotals(state.items);
    const previous = knownPacks;
    knownPacks = packs;

    if (trimGifts(state, packs)) return;

    const packId = event.variantId != null ? String(event.variantId) : '';
    const gifts = ((window.luxGiftMap || {})[packId] || []).map(String);
    if (!gifts.length) return;
    if ((packs[packId] || 0) <= (previous[packId] || 0)) return;

    const needed = (packs[packId] || 0) - countLines(state.items, gifts);
    if (needed <= 0) return;

    // On the cart page the list to refresh is the page itself, not the drawer.
    const drawer = onCartPage() ? null : document.querySelector('cart-drawer');
    addGiftLines(packId, gifts, needed, drawer)
      .then((response) => {
        if (!response) return;
        if (drawer) {
          drawer.renderContents(response);
        } else if (onCartPage()) {
          window.location.reload();
        }
        refreshKnownPacks();
      })
      .catch((error) => console.error(error));
  }

  /* One change per pass: the change goes through the cart's own
   * updateQuantity(), which re-renders the cart and publishes another
   * cartUpdate, and the next pass trims anything still over. Returns true
   * when it made a change. */
  function trimGifts(state, packs) {
    // Walk from the last line so the most recent gifts go first.
    const allowed = {};
    for (let index = state.items.length - 1; index >= 0; index -= 1) {
      const item = state.items[index];
      const packId = item.properties && item.properties._gift_for;
      if (!packId) continue;

      if (!(packId in allowed)) {
        const giftTotal = state.items
          .filter((line) => line.properties && line.properties._gift_for === packId)
          .reduce((total, line) => total + line.quantity, 0);
        allowed[packId] = giftTotal - (packs[packId] || 0);
      }
      const excess = allowed[packId];
      if (excess <= 0) continue;

      const quantity = Math.max(0, item.quantity - excess);
      const cartItems = onCartPage()
        ? document.querySelector('cart-items')
        : document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        cartItems.updateQuantity(index + 1, quantity, { currentTarget: null }, 'quantity', item.variant_id);
      } else {
        fetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: index + 1, quantity }),
        }).then(() => {
          if (onCartPage()) window.location.reload();
        });
      }
      return true;
    }
    return false;
  }

  // constants.js declares PUB_SUB_EVENTS with const, so it is a global
  // binding but not a window property; test the binding itself. Both scripts
  // are deferred ahead of this one, so they have run by now.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncGifts);
    refreshKnownPacks();
  }

  /* ---------------------------------------------------------------- *
   * Quantity stepper
   * ---------------------------------------------------------------- */

  class LuxQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-step]');
        if (!button) return;
        event.preventDefault();
        this.step(parseInt(button.dataset.step, 10));
      });
      this.input.addEventListener('change', () => this.clamp());
    }

    step(delta) {
      this.input.value = this.clampValue(this.currentValue + delta);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clamp() {
      this.input.value = this.clampValue(this.currentValue);
    }

    clampValue(value) {
      const min = parseInt(this.input.min, 10) || 0;
      const max = parseInt(this.input.max, 10) || 9999;
      return Math.min(max, Math.max(min, isNaN(value) ? min : value));
    }

    get currentValue() {
      return parseInt(this.input.value, 10);
    }
  }

  if (!customElements.get('lux-quantity')) customElements.define('lux-quantity', LuxQuantity);

  /* ---------------------------------------------------------------- *
   * Product form: variant switching, live price, quantity mirroring
   * ---------------------------------------------------------------- */

  class LuxProductForm extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-lux-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.fieldsets = Array.from(this.querySelectorAll('[data-lux-option]'));
      this.idInput = this.querySelector('[data-lux-variant-id]');
      this.priceTarget = this.querySelector('[data-lux-price]');
      this.stock = this.querySelector('[data-lux-stock]');
      this.gallery = document.querySelector('lux-gallery');

      this.quantityInput = this.querySelector('[data-lux-quantity-input]');
      this.quantityMirror = document.querySelector('[data-lux-quantity-mirror]');

      // Purchase options (one-time / subscribe). Absent on products without
      // a selling plan.
      this.purchase = this.querySelector('[data-lux-purchase]');
      this.planInput = this.querySelector('[data-lux-selling-plan]');
      this.planSelect = this.querySelector('[data-lux-plan-select]');
      if (this.purchase) {
        this.purchase.addEventListener('change', (event) => {
          if (event.target.matches('[data-lux-purchase-input], [data-lux-plan-select]')) {
            this.update(this.currentVariant, false);
          }
        });
      }

      if (this.fieldsets.length) {
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-lux-option-input]')) this.onVariantChange(event.target);
        });
      }

      document.addEventListener('change', (event) => {
        if (event.target.matches('[data-lux-quantity-input], [data-lux-quantity-mirror]')) {
          this.syncQuantity(event.target.value);
        }
      });

      this.updateOptionStates();
      this.update(this.currentVariant, false);

      this.equaliseVariantWidths();
      // Re-measure once the webfont lands, or the widths are set from the
      // fallback face and every button ends up slightly wrong.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.equaliseVariantWidths());
      }
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.equaliseVariantWidths(), 150);
      });
    }

    /* Every value in an option group takes the width of the widest one, so a
     * group reads as a tidy set rather than ragged content widths. CSS cannot
     * size siblings to the widest of them, so measure and set a custom
     * property per group. Clear it first, otherwise the second pass measures
     * the width already applied instead of the natural one.
     */
    equaliseVariantWidths() {
      if (!this.fieldsets.length) return;

      this.fieldsets.forEach((fieldset) => {
        const labels = fieldset.querySelectorAll('.lux-variant__btn');
        if (!labels.length) return;

        fieldset.style.removeProperty('--lux-variant-col');
        let widest = 0;
        labels.forEach((label) => {
          widest = Math.max(widest, Math.ceil(label.getBoundingClientRect().width));
        });
        if (widest <= 0) return;

        // When the values are too long for two to share a line, they stack
        // anyway, so let them fill the column: the buy column then reads as
        // one width from the option buttons down to the trust box, rather
        // than a 400px stack beside a full-width Add to cart.
        const row = fieldset.querySelector('.lux-variant__row');
        const rowWidth = row ? row.clientWidth : 0;
        const stacked = rowWidth > 0 && widest * 2 + 8 > rowWidth;
        fieldset.style.setProperty('--lux-variant-col', stacked ? '100%' : `${widest}px`);
      });

      this.alignPurchaseRow();
    }

    /* The purchase cards line up with the option buttons above them: as wide
     * as the span the last option group's buttons actually occupy, so a
     * stacked group of long values (400px pack sizes) gets a 400px row and a
     * wrapped group of short values gets the full column. Falls back to the
     * full column when there is no option picker. */
    alignPurchaseRow() {
      if (!this.purchase) return;
      this.purchase.style.removeProperty('--lux-purchase-col');

      const fieldset = this.fieldsets[this.fieldsets.length - 1];
      const row = fieldset && fieldset.querySelector('.lux-variant__row');
      if (!row) return;

      const left = row.getBoundingClientRect().left;
      let right = left;
      row.querySelectorAll('.lux-variant__btn').forEach((label) => {
        right = Math.max(right, label.getBoundingClientRect().right);
      });

      const width = Math.ceil(right - left);
      if (width > 0) this.purchase.style.setProperty('--lux-purchase-col', `${width}px`);
    }

    get selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('[data-lux-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    get currentVariant() {
      if (!this.fieldsets.length) return this.variants[0];
      const selected = this.selectedOptions;
      return this.variants.find((variant) => selected.every((value, index) => variant.options[index] === value));
    }

    onVariantChange(changed) {
      this.reconcile(changed);
      this.updateOptionStates();
      // Button prices may have changed with the other options, so the widest
      // value in a group may have too.
      this.equaliseVariantWidths();

      const variant = this.currentVariant;
      this.update(variant, true);

      if (variant && this.gallery && variant.media_id) {
        this.gallery.dispatchEvent(new CustomEvent('lux:variant-media', { detail: { mediaId: variant.media_id } }));
      }
    }

    /* Picking a value can strand the other options on a combination that does
     * not exist (Red comes in S and M, Blue only in L). Keep the value the
     * shopper just touched and snap the rest to a real variant.
     */
    reconcile(changed) {
      if (!this.fieldsets.length) return;

      const selected = this.selectedOptions;
      if (this.variants.some((variant) => selected.every((value, index) => variant.options[index] === value))) return;

      const index = changed ? this.fieldsets.findIndex((fieldset) => fieldset.contains(changed)) : -1;
      const keeps = (variant) => index < 0 || variant.options[index] === selected[index];
      const target = this.variants.find((variant) => variant.available && keeps(variant)) || this.variants.find(keeps);
      if (!target) return;

      this.fieldsets.forEach((fieldset, position) => {
        fieldset.querySelectorAll('[data-lux-option-input]').forEach((input) => {
          input.checked = input.value === target.options[position];
        });
      });
    }

    /* Mirrors the Liquid: a value with no matching variant is disabled, one
     * whose every match is out of stock is struck through but still clickable.
     */
    updateOptionStates() {
      if (!this.fieldsets.length) return;

      const selected = this.selectedOptions;
      this.fieldsets.forEach((fieldset, position) => {
        const priceKeys = new Set();
        fieldset.querySelectorAll('[data-lux-option-input]').forEach((input) => {
          const combination = selected.slice();
          combination[position] = input.value;
          const matches = this.variants.filter((variant) =>
            combination.every((value, index) => variant.options[index] === value)
          );

          input.disabled = matches.length === 0;
          const match = matches.find((variant) => variant.available) || matches[0];
          if (match) priceKeys.add(`${match.price_html}/${match.on_sale ? match.compare_at_html : ''}`);

          const label = fieldset.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', !matches.some((variant) => variant.available));
          this.renderOptionPrice(label, match);
        });

        // Mirrors lux-variant--flat-price in the Liquid.
        fieldset.classList.toggle('lux-variant--flat-price', this.fieldsets.length > 1 && priceKeys.size < 2);

        const current = fieldset.querySelector('[data-lux-option-current]');
        const checked = fieldset.querySelector('[data-lux-option-input]:checked');
        if (current && checked) current.textContent = checked.value;
      });
    }

    /* Price on a value button: the variant that picking it would select,
     * given the other options as they stand. Mirrors v_match in the Liquid. */
    renderOptionPrice(label, variant) {
      const price = label.querySelector('[data-lux-option-price]');
      if (!price) return;

      price.hidden = !variant;
      if (!variant) return;

      price.classList.toggle('is-on-sale', variant.on_sale);
      const amount = price.querySelector('[data-lux-option-amount]');
      const compare = price.querySelector('[data-lux-option-compare]');
      if (amount) amount.textContent = variant.price_html;
      if (compare) {
        compare.textContent = variant.compare_at_html;
        compare.hidden = !variant.on_sale;
      }

      // Per-treatment price, for variants whose title carries a treatment count.
      const unit = price.querySelector('[data-lux-option-unit]');
      const perTemplate = label.closest('[data-text-per-unit]');
      if (unit) {
        const units = parseInt(variant.units, 10) || 0;
        unit.hidden = !(units > 0 && perTemplate);
        if (!unit.hidden) {
          unit.textContent = perTemplate.dataset.textPerUnit.replace('[amount]', formatMoney(Math.round(variant.price / units)));
        }
      }

      const save = price.querySelector('[data-lux-option-save]');
      const template = label.closest('[data-text-save]');
      if (save) {
        save.hidden = !variant.on_sale;
        if (variant.on_sale && template) {
          // Same rounding as the Liquid's v_discount; amount without trailing
          // zeros, like money_without_trailing_zeros.
          const saved = variant.compare_at_price - variant.price;
          const percent = Math.round((saved * 100) / variant.compare_at_price);
          const amount = formatMoney(saved).replace(/([.,]00)(?!\d)/, '');
          save.textContent = template.dataset.textSave.replace('[amount]', amount).replace('[percent]', percent);
        }
      }
    }

    /* Purchase options ---------------------------------------------------- */

    get subscribing() {
      if (!this.purchase) return false;
      const checked = this.purchase.querySelector('[data-lux-purchase-input]:checked');
      return !!checked && checked.value === 'subscribe';
    }

    /* The selling plan the cart should get for this variant: the chosen
     * frequency when it exists for the variant, otherwise the variant's
     * first plan (and the dropdown is moved to match). Null for one-time. */
    currentPlan(variant) {
      if (!variant || !this.subscribing) return null;
      const plans = variant.selling_plans || [];
      if (!plans.length) return null;

      const wanted = this.planSelect ? this.planSelect.value : '';
      const plan = plans.find((candidate) => String(candidate.id) === wanted) || plans[0];
      if (this.planSelect && this.planSelect.value !== String(plan.id)) this.planSelect.value = plan.id;
      return plan;
    }

    /* What the shopper pays per unit for the current choice. */
    offer(variant, plan) {
      if (!plan) return variant;
      return {
        price: plan.price,
        price_html: plan.price_html,
        compare_at_price: variant.price,
        compare_at_html: variant.price_html,
        on_sale: plan.price < variant.price,
      };
    }

    /* Mirrors the Liquid: the subscribe card only exists for a variant that
     * has a plan, its price follows the chosen frequency, and the frequency
     * panel shows while subscribe is selected. */
    renderPurchase(variant) {
      if (!this.purchase) return;

      const plans = variant.selling_plans || [];
      const subInput = this.purchase.querySelector('#LuxPurchase-sub');
      const subCard = this.purchase.querySelector('[data-lux-purchase-sub]');
      const onceInput = this.purchase.querySelector('#LuxPurchase-once');

      if (subInput) subInput.hidden = !plans.length;
      if (subCard) subCard.hidden = !plans.length;
      if (!plans.length && subInput && subInput.checked && onceInput) onceInput.checked = true;

      if (this.planSelect) {
        const ids = new Set(plans.map((plan) => String(plan.id)));
        Array.from(this.planSelect.options).forEach((option) => {
          option.disabled = !ids.has(option.value);
          option.hidden = option.disabled;
        });
      }

      const once = this.purchase.querySelector('[data-lux-purchase-once-amount]');
      if (once) once.textContent = variant.price_html;

      // Chosen frequency if subscribing, else the first plan: the card shows
      // the saving before the shopper commits to it.
      const shown = this.currentPlan(variant) || plans[0];
      if (shown) {
        const percent = variant.price > 0 ? Math.round(((variant.price - shown.price) * 100) / variant.price) : 0;
        const amount = this.purchase.querySelector('[data-lux-purchase-sub-amount]');
        const compare = this.purchase.querySelector('[data-lux-purchase-sub-compare]');
        const save = this.purchase.querySelector('[data-lux-purchase-save]');
        if (amount) amount.textContent = shown.price_html;
        if (compare) {
          compare.textContent = variant.price_html;
          compare.hidden = percent <= 0;
        }
        if (save) save.textContent = (this.purchase.dataset.textSave || '').replace('[percent]', percent);
      }

      const panel = this.purchase.querySelector('[data-lux-purchase-plan]');
      if (panel) panel.hidden = !this.subscribing;

      // Legend reads "Purchase · One-time purchase", like the option legends.
      const current = this.purchase.querySelector('[data-lux-purchase-current]');
      const checked = this.purchase.querySelector('[data-lux-purchase-input]:checked');
      const checkedTitle = checked && this.purchase.querySelector(`label[for="${checked.id}"] .lux-purchase__title`);
      if (current && checkedTitle) current.textContent = checkedTitle.textContent.trim();
    }

    update(variant, updateUrl) {
      if (!variant) return;

      if (this.idInput) this.idInput.value = variant.id;

      this.renderPurchase(variant);
      const plan = this.currentPlan(variant);
      if (this.planInput) this.planInput.value = plan ? plan.id : '';

      // Subscribing: the plan price leads and the one-time price is the
      // struck comparison, matching the subscribe card.
      const offer = this.offer(variant, plan);
      if (this.priceTarget) {
        const amount = this.priceTarget.querySelector('.lux-price__amount');
        const compare = this.priceTarget.querySelector('.lux-price__compare');
        const price = this.priceTarget.querySelector('.lux-price');
        if (amount) amount.textContent = offer.price_html;
        if (price) price.classList.toggle('lux-price--on-sale', offer.on_sale);
        if (compare) {
          compare.textContent = offer.compare_at_html;
          compare.hidden = !offer.on_sale;
        }
      }

      document.querySelectorAll('[data-lux-add-button]').forEach((button) => {
        button.disabled = !variant.available;

        const label = button.querySelector('[data-lux-add-label]');
        if (label) label.textContent = variant.available ? window.luxStrings.addToCart : window.luxStrings.soldOut;

        const total = button.querySelector('[data-lux-add-total]');
        if (total) total.hidden = !variant.available;
      });

      this.renderTotal(variant, this.quantity);
      this.renderStock(variant);
      this.renderYouSave(offer);

      if (updateUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    get quantity() {
      return Math.max(1, parseInt(this.quantityInput && this.quantityInput.value, 10) || 1);
    }

    syncQuantity(value) {
      const quantity = Math.max(1, parseInt(value, 10) || 1);
      if (this.quantityInput && this.quantityInput.value !== String(quantity)) {
        this.quantityInput.value = quantity;
      }
      if (this.quantityMirror && this.quantityMirror.value !== String(quantity)) {
        this.quantityMirror.value = quantity;
      }
      this.renderTotal(this.currentVariant, quantity);
    }

    /* Mirrors the Liquid in lux-main-product.liquid: nothing for untracked
     * inventory or variants that keep selling when out of stock. */
    renderStock(variant) {
      const el = this.stock;
      if (!el) return;

      if (!variant.inventory_managed || variant.inventory_policy === 'continue') {
        el.hidden = true;
        el.dataset.state = '';
        return;
      }

      const quantity = parseInt(variant.inventory_quantity, 10) || 0;
      const threshold = parseInt(el.dataset.threshold, 10) || 0;
      let state = 'out';
      let text = el.dataset.textOut;

      if (quantity > threshold) {
        state = 'in';
        text = el.dataset.textIn;
      } else if (quantity > 0) {
        state = 'low';
        text = (el.dataset.textLow || '').replace('[count]', quantity);
      }

      el.hidden = false;
      el.dataset.state = state;
      const target = el.querySelector('[data-lux-stock-text]');
      if (target) target.textContent = text;
    }

    /* "You save $X against the regular price of $Y." Mirrors the Liquid: the
     * compare-at for one-time, the one-time price while subscribing. The
     * template carries <b> tags and the amounts come from formatMoney. */
    renderYouSave(offer) {
      const el = this.querySelector('[data-lux-you-save]');
      if (!el) return;
      const compare = parseInt(offer.compare_at_price, 10) || 0;
      if (!(compare > offer.price)) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      el.innerHTML = (el.dataset.text || '')
        .replace('[amount]', formatMoney(compare - offer.price))
        .replace('[compare]', formatMoney(compare));
    }

    renderTotal(variant, quantity) {
      if (!variant || !variant.available) return;
      const total = this.offer(variant, this.currentPlan(variant)).price * (quantity || 1);
      document.querySelectorAll('[data-lux-add-total]').forEach((node) => {
        node.textContent = `· ${formatMoney(total)}`;
      });
    }
  }

  if (!customElements.get('lux-product-form')) customElements.define('lux-product-form', LuxProductForm);

  /* Formats cents with the money format of the currency the customer is
   * browsing in. Shopify does not ship a storefront Shopify.formatMoney, so
   * this implements the documented format tokens itself; the old fallback
   * hard-coded "$" and showed it in every market. */
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.luxStrings.moneyFormat);
    }

    const format = (window.luxStrings && window.luxStrings.moneyFormat) || '${{amount}}';
    const amount = (precision, thousands, decimal) => {
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    const html = format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return amount(0, ',', '.');
        case 'amount_with_comma_separator':
          return amount(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return amount(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return amount(2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return amount(0, ' ', ',');
        case 'amount_with_space_separator':
          return amount(2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return amount(2, ' ', '.');
        default:
          return amount(2, ',', '.');
      }
    });

    // The format may carry markup or entities (e.g. "&euro;"); callers set textContent.
    const scratch = document.createElement('template');
    scratch.innerHTML = html;
    return scratch.content.textContent.trim();
  }

  /* ---------------------------------------------------------------- *
   * App slot: pull third-party product widgets under the Why Lux block
   * ---------------------------------------------------------------- */

  const NON_VISUAL = new Set(['STYLE', 'SCRIPT', 'LINK', 'META', 'TEMPLATE', 'NOSCRIPT', 'TITLE']);

  class LuxAppSlot extends HTMLElement {
    connectedCallback() {
      this.selector = (this.dataset.selector || '').trim();
      if (!this.selector) return;

      this.adopt();

      // Apps inject late and re-render after variant changes, so keep
      // watching for a while rather than checking once on load.
      this.observer = new MutationObserver(() => {
        if (this.pending) return;
        this.pending = true;
        // Deliberately not requestAnimationFrame: it does not fire while the
        // tab is in the background, which is exactly when an app is most
        // likely to finish injecting.
        this.debounce = setTimeout(() => {
          this.pending = false;
          this.adopt();
        }, 16);
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.timer = setTimeout(() => this.stop(), 30000);
    }

    disconnectedCallback() {
      this.stop();
    }

    stop() {
      clearTimeout(this.timer);
      clearTimeout(this.debounce);
      this.pending = false;
      if (this.observer) this.observer.disconnect();
      this.observer = null;
    }

    adopt() {
      let matches;
      try {
        matches = document.querySelectorAll(this.selector);
      } catch (error) {
        this.stop(); // a bad selector from the setting: give up quietly
        return;
      }

      matches.forEach((match) => {
        const widget = this.outermost(match);
        if (!widget || widget === this || this.contains(widget) || widget.contains(this)) return;

        // Apps put ids on their <style> and <script> tags too. Those belong in
        // <head>; only move things that actually render.
        if (NON_VISUAL.has(widget.tagName)) return;

        // Overlays are positioned against the viewport and break if reparented.
        const position = getComputedStyle(widget).position;
        if (position === 'fixed' || position === 'sticky') return;
        if (/modal|popup|overlay|drawer|tooltip/i.test(widget.className || '')) return;

        // A broad selector can land on something wrapping our own markup
        // (an app class on <body>, or a wrapper around the buy column).
        // Never relocate anything holding theme-owned product UI.
        if (widget.querySelector('lux-product-form, .lux-pdp__grid, lux-sticky-buy')) return;

        this.appendChild(widget);
      });
    }

    /* A selector can land on a piece inside the widget rather than the widget
     * itself, and moving pieces individually flattens it — the totals row and
     * its button end up as siblings of the cards instead of a row beneath
     * them. Climb to the outermost wrapper still owned by the same app.
     *
     * Selleasy mixes two prefixes on the same nodes ("ppcse-title … lb-upsell-
     * title") and its outer container carries only the ppcse one, so matching
     * lb- alone stopped the climb one level too low.
     */
    outermost(node) {
      const owned = (el) => typeof el.className === 'string' && /(^|\s)(lb-|ppcs|selleasy)/.test(el.className);
      let top = node;
      let parent = node.parentElement;
      while (parent && parent !== document.body && owned(parent)) {
        top = parent;
        parent = parent.parentElement;
      }
      return top;
    }
  }

  if (!customElements.get('lux-app-slot')) customElements.define('lux-app-slot', LuxAppSlot);

  /* ---------------------------------------------------------------- *
   * Sticky buy bar
   * ---------------------------------------------------------------- */

  class LuxStickyBuy extends HTMLElement {
    connectedCallback() {
      const inline = document.querySelector('.lux-pdp__form .lux-pdp__buy');
      if (!inline || !('IntersectionObserver' in window)) return;

      // Both hold the same Add to cart, so stand down while the inline row
      // is on screen rather than showing the shopper two of them.
      this.observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-hidden', entry.isIntersecting);
      });
      this.observer.observe(inline);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('lux-sticky-buy')) customElements.define('lux-sticky-buy', LuxStickyBuy);

  /* ---------------------------------------------------------------- *
   * Product gallery (crossfade + thumbs + dots)
   * ---------------------------------------------------------------- */

  class LuxGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-lux-slide]'));
      this.controls = Array.from(this.querySelectorAll('[data-lux-goto]'));
      this.index = 0;

      this.controls.forEach((control) => {
        control.addEventListener('click', () => this.show(parseInt(control.dataset.luxGoto, 10)));
      });

      this.addEventListener('lux:variant-media', (event) => {
        const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(event.detail.mediaId));
        if (target > -1) this.show(target);
      });

      const stage = this.querySelector('.lux-pdp__stage') || this;
      stage.addEventListener(
        'touchstart',
        (event) => {
          this.touchStartX = event.changedTouches[0].clientX;
          this.touchStartY = event.changedTouches[0].clientY;
        },
        { passive: true }
      );
      stage.addEventListener(
        'touchend',
        (event) => {
          const deltaX = event.changedTouches[0].clientX - this.touchStartX;
          const deltaY = event.changedTouches[0].clientY - this.touchStartY;
          if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
          this.show(this.index + (deltaX < 0 ? 1 : -1));
        },
        { passive: true }
      );
    }

    show(index) {
      if (index === this.index || index < 0 || index >= this.slides.length) return;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.controls.forEach((control) => {
        const active = parseInt(control.dataset.luxGoto, 10) === index;
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-current', active ? 'true' : 'false');
      });
      this.index = index;
    }
  }

  if (!customElements.get('lux-gallery')) customElements.define('lux-gallery', LuxGallery);

  /* ---------------------------------------------------------------- *
   * Product recommendations
   * ---------------------------------------------------------------- */

  class LuxRecommendations extends HTMLElement {
    connectedCallback() {
      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.load();
      };

      new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }

    load() {
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const incoming = new DOMParser().parseFromString(text, 'text/html').querySelector('lux-recommendations');
          if (incoming && incoming.innerHTML.trim().length) this.innerHTML = incoming.innerHTML;
        })
        .catch((error) => console.error(error));
    }
  }

  if (!customElements.get('lux-recommendations')) customElements.define('lux-recommendations', LuxRecommendations);

  /* ---------------------------------------------------------------- *
   * Tabs
   * ---------------------------------------------------------------- */

  class LuxTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = event.key === 'ArrowRight' ? index + 1 : index - 1;
          const bounded = (next + this.tabs.length) % this.tabs.length;
          this.select(bounded);
          this.tabs[bounded].focus();
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }
  }

  if (!customElements.get('lux-tabs')) customElements.define('lux-tabs', LuxTabs);

  /* ---------------------------------------------------------------- *
   * Overlay surfaces: mobile menu drawer + filter bottom sheet
   * ---------------------------------------------------------------- */

  class LuxOverlay extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-lux-panel]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-lux-close]') || !event.target.closest('[data-lux-panel]')) {
          this.close();
        }
      });
      this.keyHandler = (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      };
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      document.body.classList.add('lux-overlay-open');
      document.addEventListener('keydown', this.keyHandler);
      requestAnimationFrame(() => {
        const first = this.panel && this.panel.querySelector(focusableSelector);
        if (first) first.focus();
      });
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.body.classList.remove('lux-overlay-open');
      document.removeEventListener('keydown', this.keyHandler);
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    }

    trapFocus(event) {
      if (!this.panel) return;
      const focusable = Array.from(this.panel.querySelectorAll(focusableSelector)).filter(
        (element) => element.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('lux-overlay')) customElements.define('lux-overlay', LuxOverlay);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-lux-open]');
    if (!trigger) return;
    const overlay = document.getElementById(trigger.dataset.luxOpen);
    if (!overlay) return;
    event.preventDefault();
    overlay.open(trigger);
  });

  /* ---------------------------------------------------------------- *
   * Collection facets (Shopify native filters, async section render)
   * ---------------------------------------------------------------- */

  class LuxFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.debounced = debounceFn(() => this.submit(), 400);

      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-lux-facet-form]')) this.debounced();
      });

      this.addEventListener('click', (event) => {
        const clear = event.target.closest('[data-lux-facet-clear]');
        if (!clear) return;
        event.preventDefault();
        this.render(clear.getAttribute('href'));
      });

      this.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });

      window.addEventListener('popstate', () => this.render(window.location.href, false));
    }

    submit() {
      const params = new URLSearchParams();

      // The filter controls are rendered twice (desktop sidebar + mobile sheet).
      // Only the copy that is actually laid out contributes, so the two never
      // fight over the same parameter.
      this.querySelectorAll('[data-lux-facet-form] [name]').forEach((control) => {
        if (control.disabled || control.offsetParent === null) return;
        if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) return;
        if (control.value === '') return;
        params.append(control.name, control.value);
      });

      const query = params.toString();
      this.render(query ? `${window.location.pathname}?${query}` : window.location.pathname);
    }

    render(url, pushState = true) {
      this.classList.add('lux-facets--loading');
      const grid = document.getElementById('LuxProductGrid');
      if (grid) grid.classList.add('is-loading');

      const [path, search = ''] = url.split('?');
      const fetchUrl = `${path}?section_id=${this.sectionId}&${search}`;

      fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');

          ['LuxProductGrid', 'LuxFacetPanel', 'LuxProductCount', 'LuxActiveFacets', 'LuxFacetSheetBody'].forEach(
            (id) => {
              const incoming = parsed.getElementById(id);
              const current = document.getElementById(id);
              if (incoming && current) current.innerHTML = incoming.innerHTML;
            }
          );

          if (pushState) window.history.pushState({ url }, '', url);
        })
        .catch((error) => console.error(error))
        .finally(() => {
          this.classList.remove('lux-facets--loading');
          const updatedGrid = document.getElementById('LuxProductGrid');
          if (updatedGrid) updatedGrid.classList.remove('is-loading');
        });
    }
  }

  if (!customElements.get('lux-facets')) customElements.define('lux-facets', LuxFacets);

  function debounceFn(fn, wait) {
    let timer;
    return function debounced() {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  /* ---------------------------------------------------------------- *
   * Header: hover-intent mega menu
   * ---------------------------------------------------------------- */

  class LuxMegaMenu extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-lux-mega-trigger]');
      this.panel = this.querySelector('[data-lux-mega-panel]');
      if (!this.trigger || !this.panel) return;

      this.addEventListener('mouseenter', () => this.show());
      this.addEventListener('mouseleave', () => this.hide(160));
      this.addEventListener('focusin', () => this.show());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hide(0);
      });
      this.trigger.addEventListener('click', (event) => {
        if (this.hasAttribute('open')) return;
        event.preventDefault();
        this.show();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.hide(0);
      });
    }

    show() {
      clearTimeout(this.timer);
      this.setAttribute('open', '');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    hide(delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.removeAttribute('open');
        this.trigger.setAttribute('aria-expanded', 'false');
      }, delay);
    }
  }

  if (!customElements.get('lux-mega-menu')) customElements.define('lux-mega-menu', LuxMegaMenu);

  /* ---------------------------------------------------------------- *
   * Currency / language selector popover
   * The options are submit buttons in Shopify's localization form, so
   * this only opens and closes the panel and filters long country lists.
   * ---------------------------------------------------------------- */

  class LuxLocalization extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-lux-locale-toggle]');
      this.panel = this.querySelector('[data-lux-locale-panel]');
      this.filter = this.querySelector('[data-lux-locale-filter]');
      if (!this.toggle || !this.panel || this.bound) return;
      this.bound = true;

      this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        // Keep Escape from also closing the menu drawer the selector sits in.
        event.stopPropagation();
        this.close();
        this.toggle.focus();
      });

      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });

      this.outsideHandler = (event) => {
        if (!this.contains(event.target)) this.close();
      };

      if (this.filter) {
        this.filter.addEventListener('input', () => this.applyFilter());
        // Enter would otherwise submit the form through its first country button.
        this.filter.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') event.preventDefault();
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.outsideHandler);
    }

    get isOpen() {
      return !this.panel.hidden;
    }

    open() {
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.outsideHandler);
      const current = this.panel.querySelector('[aria-current="true"]');
      if (current) current.scrollIntoView({ block: 'nearest' });
      if (this.filter && window.matchMedia('(hover: hover)').matches) this.filter.focus();
    }

    close() {
      if (!this.isOpen) return;
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.outsideHandler);
      if (this.filter && this.filter.value) {
        this.filter.value = '';
        this.applyFilter();
      }
    }

    applyFilter() {
      const query = this.filter.value.trim().toLowerCase();
      this.querySelectorAll('[data-lux-locale-item]').forEach((item) => {
        item.hidden = query !== '' && !item.dataset.name.includes(query);
      });
    }
  }

  if (!customElements.get('lux-localization')) customElements.define('lux-localization', LuxLocalization);
})();
